import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { SocialPlatformIcon } from '@app/modules/social-export/components/SocialPlatformIcon';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportGetConnectionById } from '@mi-front/api-v2';
import { Avatar, Stack, Typography } from '@mui/material';
import { UploadCardContextSeparator } from './UploadCardContextSeparator';
import { UploadCardEditIndicator } from './UploadCardEditIndicator';
import { UploadCardDto } from './useUploadCardActions';

interface UploadCardContextBarProps {
  upload: UploadCardDto;
}

function getEditFromUpload(upload: UploadCardDto): { name: string } | null {
  if ('edit' in upload && upload.edit) {
    return upload.edit;
  }
  return null;
}

/**
 * Top-bar context: "Platform • [avatar] Profile • [edit chip]" with consistent separators.
 */
export function UploadCardContextBar({ upload }: UploadCardContextBarProps) {
  const platform = socialPlatforms.get(upload.platform);
  // Generated OpenAPI types ObjectIds as `object`; runtime JSON values are strings.
  const connectionId = typeof upload.connectionId === 'string' ? upload.connectionId : undefined;
  const edit = getEditFromUpload(upload);

  const connectionQuery = useSocialExportGetConnectionById(connectionId ?? '', {
    query: { enabled: !!connectionId },
  });

  const connection = connectionQuery.data;
  const profilePhoto = connection?.profile.photo;
  const avatarInitial = connection
    ? (connection.name || connection.profile.displayName).charAt(0)
    : null;

  return (
    <Stack direction="row" alignItems="center" gap={1.5} minWidth={0} flexWrap="wrap" useFlexGap>
      <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
        <SocialPlatformIcon platform={platform} size="lg" />
        <Typography variant="subtitle2" color="text.secondary" noWrap>
          {platform.name}
        </Typography>
      </Stack>
      {connection ? (
        <>
          <UploadCardContextSeparator />
          <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
            <Avatar
              src={profilePhoto || undefined}
              sx={{ width: 20, height: 20, typography: 'caption' }}
            >
              {!profilePhoto ? avatarInitial : null}
            </Avatar>
            <ConnectionDisplayName
              connection={connection}
              variant="subtitle2"
              color="text.secondary"
              noWrap
              component="span"
            />
          </Stack>
        </>
      ) : null}
      {edit ? (
        <>
          <UploadCardContextSeparator />
          <UploadCardEditIndicator edit={edit} />
        </>
      ) : null}
    </Stack>
  );
}
